import { Box, Drawer as MuiDrawer, Typography } from '@mui/material';
import React, { createContext, useMemo, useReducer } from 'react';

import IconButton from '@components/IconButton';

import { useIsMobile } from '@hooks/useIsMobile';

interface DrawerState {
  isOpen: boolean;
  content: React.ReactNode;
  title: React.ReactNode;
}

export enum DrawerActions {
  SetOpen = 'setOpen',
  SetContent = 'setContent',
  SetTitle = 'setTitle',
  Reset = 'reset',
}

type DrawerPayload = {
  [DrawerActions.SetOpen]: {
    isOpen: boolean;
  };
  [DrawerActions.SetContent]: {
    content: React.ReactNode;
  };
  [DrawerActions.SetTitle]: {
    title: React.ReactNode;
  };
  [DrawerActions.Reset]: undefined;
};

interface DrawerContext {
  state: DrawerState;
  openDrawer: () => void;
  closeDrawer: () => void;
  setContent: (content: React.ReactNode) => void;
  setTitle: (title: React.ReactNode) => void;
  dispatch: React.Dispatch<Action<DrawerPayload>>;
}

const initialDrawerState: DrawerState = {
  isOpen: false,
  content: null,
  title: 'Title',
};

function reducer(
  state: DrawerState,
  action: Action<DrawerPayload>
): DrawerState {
  switch (action.type) {
    case DrawerActions.SetOpen:
      return {
        ...state,
        isOpen: action.isOpen,
      };
    case DrawerActions.SetContent:
      return {
        ...state,
        content: action.content,
      };
    case DrawerActions.SetTitle:
      return {
        ...state,
        title: action.title,
      };
    case DrawerActions.Reset:
      return initialDrawerState;
  }
}

export const DrawerContext = createContext<DrawerContext>({
  state: initialDrawerState,
  dispatch: () => null,
  setContent: () => null,
  openDrawer: () => null,
  closeDrawer: () => null,
  setTitle: () => null,
});
DrawerContext.displayName = 'DrawerContext';

export const DrawerProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children = null }) => {
  const [state, dispatch] = useReducer(reducer, initialDrawerState);
  const { isOpen, content } = state;
  const isMobile = useIsMobile();

  const openDrawer = React.useCallback(() => {
    dispatch({ type: DrawerActions.SetOpen, isOpen: true });
  }, []);

  const closeDrawer = React.useCallback(() => {
    dispatch({ type: DrawerActions.SetOpen, isOpen: false });
  }, []);

  const setContent = React.useCallback((content: React.ReactNode) => {
    dispatch({ type: DrawerActions.SetContent, content });
  }, []);

  const setTitle = React.useCallback((title: React.ReactNode) => {
    dispatch({ type: DrawerActions.SetTitle, title });
  }, []);

  const value: DrawerContext = useMemo(
    () => ({ state, openDrawer, closeDrawer, setContent, setTitle, dispatch }),
    [closeDrawer, openDrawer, setContent, setTitle, state]
  );

  return (
    <DrawerContext.Provider value={value}>
      <MuiDrawer
        anchor="right"
        variant="persistent"
        sx={{
          width: 280,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 280,
            marginTop: { xs: '0px', lg: '52px' },
            boxSizing: 'border-box',
            backgroundColor: (theme) => theme.palette.divider,
            borderRadius: 0,
            boxShadow: isMobile ? ({ shadows }) => shadows[8] : undefined,
          },
        }}
        open={isOpen}
        onClose={closeDrawer}
      >
        <Box
          sx={{
            overflow: 'auto',
            padding: 1.25,
            height: '90%',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography color="primary.main" variant="h5">
              {state.title}
            </Typography>
            <IconButton
              color="text.secondary"
              iconType="fad"
              name="chevron-right"
              onClick={closeDrawer}
            />
          </Box>

          {content}
        </Box>
      </MuiDrawer>
      {children}
    </DrawerContext.Provider>
  );
};
